import React, { useEffect, useState } from "react";
import { Table, Button, Space, Tag, Switch, Modal, message, Image, Tooltip, Badge } from "antd";
import { useNavigate } from "react-router-dom";
import bannerApi from "../../../api/bannerApi";
import { audienceColumn } from "../../../components/audience-fields";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faImage } from "@fortawesome/free-solid-svg-icons";

const API_IMG_URL = process.env.REACT_APP_API_URL?.replace("/api/v1", "") || "";

/** Compute current display status based on isActive + startTime/endTime vs now */
function getBannerStatus(banner) {
    if (!banner.isActive) return { text: "Đã tắt", color: "default" };
    const now = Math.floor(Date.now() / 1000);
    if (banner.startTime && banner.startTime > now) return { text: "Chưa đến giờ", color: "orange" };
    if (banner.endTime && banner.endTime < now) return { text: "Hết hạn", color: "red" };
    return { text: "Đang hiển thị", color: "green" };
}

const DISPLAY_TARGET_LABELS = {
    all: { text: "Tất cả", color: "blue" },
    web: { text: "Web", color: "green" },
    app: { text: "App", color: "orange" },
};

function BannerList() {
    const navigate = useNavigate();
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchBanners = async () => {
        try {
            setLoading(true);
            const { data } = await bannerApi.getAll();
            setBanners(data.data || []);
        } catch {
            message.error("Không thể tải danh sách banner");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchBanners(); }, []);

    const activeCount = banners.filter((b) => b.isActive).length;

    const handleToggle = async (id) => {
        try {
            await bannerApi.toggle(id);
            message.success("Đã cập nhật trạng thái");
            fetchBanners();
        } catch {
            message.error("Không thể cập nhật trạng thái");
        }
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: "Xác nhận xóa",
            content: "Bạn có chắc muốn xóa banner này?",
            okText: "Xóa",
            cancelText: "Hủy",
            okType: "danger",
            onOk: async () => {
                try {
                    await bannerApi.delete(id);
                    message.success("Đã xóa banner");
                    fetchBanners();
                } catch {
                    message.error("Không thể xóa banner");
                }
            },
        });
    };

    const formatDate = (ts) => {
        if (!ts) return "—";
        return format(new Date(ts * 1000), "dd/MM/yy HH:mm");
    };

    const columns = [
        { title: "ID", dataIndex: "bannerId", key: "bannerId", width: 55 },
        {
            title: "Ảnh",
            dataIndex: "imagePath",
            key: "imagePath",
            width: 90,
            render: (path) => (
                <Image
                    src={`${API_IMG_URL}${path}`}
                    alt="banner"
                    width={72}
                    height={48}
                    style={{ objectFit: "cover", borderRadius: 6 }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                />
            ),
        },
        { title: "Tiêu đề", dataIndex: "title", key: "title", ellipsis: true },
        {
            title: "Trạng thái",
            key: "displayStatus",
            width: 130,
            render: (_, record) => {
                const s = getBannerStatus(record);
                return <Tag color={s.color}>{s.text}</Tag>;
            },
        },
        {
            title: "Hiển thị",
            dataIndex: "displayTarget",
            key: "displayTarget",
            width: 90,
            render: (target) => {
                const info = DISPLAY_TARGET_LABELS[target] || { text: target, color: "default" };
                return <Tag color={info.color}>{info.text}</Tag>;
            },
        },
        audienceColumn(),
        {
            title: "Link",
            dataIndex: "linkUrl",
            key: "linkUrl",
            ellipsis: true,
            width: 170,
            render: (url, record) =>
                url ? (
                    <span>
                        <Tag color={record.linkType === "external" ? "red" : "blue"} className="mr-1">
                            {record.linkType}
                        </Tag>
                        <span className="text-xs text-content-3">{url}</span>
                    </span>
                ) : (
                    <span className="text-content-3">—</span>
                ),
        },
        {
            title: "Ưu tiên",
            dataIndex: "priority",
            key: "priority",
            width: 70,
            align: "center",
            sorter: (a, b) => a.priority - b.priority,
        },
        {
            title: "Thời gian",
            key: "timeRange",
            width: 155,
            render: (_, record) => (
                <div className="text-xs leading-5 text-content-3">
                    <div>Từ: {formatDate(record.startTime)}</div>
                    <div>Đến: {formatDate(record.endTime)}</div>
                </div>
            ),
        },
        {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            width: 75,
            align: "center",
            render: (isActive, record) => (
                <Switch checked={isActive} onChange={() => handleToggle(record.bannerId)} size="small" />
            ),
        },
        {
            title: "Hành động",
            key: "action",
            width: 90,
            fixed: "right",
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Sửa">
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faEdit} />}
                            onClick={() => navigate(`/banner/${record.bannerId}/edit`)}
                        />
                    </Tooltip>
                    <Tooltip title="Xóa">
                        <Button
                            size="small"
                            danger
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={() => handleDelete(record.bannerId)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="flex-1 p-6 overflow-auto">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
                <h1 className="text-2xl font-bold">Quản lý Banner Popup</h1>
                <Button
                    type="primary"
                    icon={<FontAwesomeIcon icon={faPlus} className="mr-1" />}
                    onClick={() => navigate("/banner/create")}
                >
                    Tạo mới
                </Button>
            </div>

            {/* Stats row */}
            <div className="flex gap-3 mb-5">
                {[
                    { icon: faImage, iconClass: "text-blue-400", value: banners.length, label: "Tổng banner", valueClass: "text-content" },
                    { badge: "success", value: activeCount, label: "Đang bật", valueClass: "text-green-600" },
                    { badge: "default", value: banners.length - activeCount, label: "Đã tắt", valueClass: "text-content-3" },
                ].map((stat, i) => (
                    <div key={i} className="bg-surface rounded-lg border border-line shadow-sm px-4 py-3 flex items-center gap-3 min-w-[110px]">
                        {stat.icon ? (
                            <FontAwesomeIcon icon={stat.icon} className={`text-xl ${stat.iconClass}`} />
                        ) : (
                            <Badge status={stat.badge} />
                        )}
                        <div>
                            <div className={`text-2xl font-bold ${stat.valueClass}`}>{stat.value}</div>
                            <div className="text-xs text-content-3">{stat.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            <Table
                columns={columns}
                dataSource={banners}
                loading={loading}
                rowKey="bannerId"
                pagination={{ pageSize: 20 }}
                scroll={{ x: "max-content" }}
                size="middle"
            />
        </div>
    );
}

export default BannerList;
